
import { useEffect, useState, useMemo } from "react";
import { TikTokVideo } from "@/types/api";
import { Table, TableBody } from "@/components/ui/table";
import { DownloadControls } from "@/components/download/DownloadControls";
import { useBulkDownload } from "@/hooks/useBulkDownload";
import { VideoTableRow } from "./video/VideoTableRow";
import { calculateVideoStats, formatDuration } from "@/utils/videoHelpers";
import { VideoTableHeader } from "./video/VideoTableHeader";
import { VideoPagination } from "./video/VideoPagination";
import { VideoLoadingIndicator } from "./video/VideoLoadingIndicator";
import { VideoCard } from "./video/VideoCard";

interface VideoResultsProps {
  videos: TikTokVideo[];
  isLoading?: boolean;
  isFetchingMore?: boolean;
  hasMore?: boolean;
  onLoadMore?: () => void;
  collectionUrl?: string;
}

export default function VideoResults({
  videos,
  isLoading = false,
  isFetchingMore = false,
  collectionUrl = "",
}: VideoResultsProps) {
  const [selected, setSelected] = useState<string[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  const [localVideos, setLocalVideos] = useState<TikTokVideo[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid');
  const itemsPerPage = 30;

  const {
    progressState,
    isBulkDownloading,
    handleBulkDownload,
    handlePauseAll,
    handleResumeAll,
    handleCancelAll,
    handleRetryFailedBulk,
    handleDownloadZip,
    downloadList,
    progressCounts,
  } = useBulkDownload(localVideos, selected);

  const isCompletedAll = useMemo(() => {
    const relevantIds = downloadList.map(v => v.id);
    return relevantIds.length > 0 &&
      relevantIds.every(id =>
        progressState[id]?.status === "complete" ||
        progressState[id]?.status === "failed"
      );
  }, [progressState, downloadList]);

  const videoStats = useMemo(() => 
    calculateVideoStats(localVideos), [localVideos]);

  useEffect(() => {
    setLocalVideos(videos);
  }, [videos]);

  useEffect(() => {
    setSelectAll(selected.length === localVideos.length && localVideos.length > 0);
  }, [selected, localVideos]);

  function toggleSelect(id: string) {
    setSelected(old => old.includes(id) ? old.filter(i => i !== id) : [...old, id]);
  }

  function handleDeleteVideo(id: string) {
    setLocalVideos(prev => prev.filter(v => v.id !== id));
    setSelected(prev => prev.filter(s => s !== id));
  }

  function handleViewModeChange(mode: 'grid' | 'table') {
    setViewMode(mode);
  }

  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, localVideos.length);
  const currentVideos = localVideos.slice(startIndex, endIndex);

  return (
    <div className="w-full max-w-6xl mx-auto">
      <VideoLoadingIndicator 
        isLoading={isLoading}
        isFetchingMore={isFetchingMore}
        videosCount={localVideos.length}
      />

      <DownloadControls
        videos={localVideos}
        selected={selected}
        setSelected={setSelected}
        isBulkDownloading={isBulkDownloading}
        progressCounts={progressCounts}
        onDownloadAll={handleBulkDownload}
        onPauseAll={handlePauseAll}
        onResumeAll={handleResumeAll}
        onCancelAll={handleCancelAll}
        onDownloadZip={handleDownloadZip}
        onRetryFailedBulk={handleRetryFailedBulk}
        isCompletedAll={isCompletedAll}
        collectionUrl={collectionUrl}
        viewMode={viewMode}
        onViewModeChange={handleViewModeChange}
      />

      <div className="rounded-lg border border-gray-800/30 overflow-hidden bg-[#0F111A]">
        {viewMode === 'table' ? (
          <div className="overflow-x-auto">
            <Table className="min-w-full">
              <VideoTableHeader 
                selectAll={selectAll}
                onToggleSelectAll={() => {
                  if (selectAll) {
                    setSelected([]);
                  } else {
                    setSelected(localVideos.map(v => v.id));
                  }
                }}
              />
              <TableBody>
                {currentVideos.map((video, index) => (
                  <VideoTableRow
                    key={video.id}
                    video={video}
                    order={startIndex + index + 1}
                    isChecked={selected.includes(video.id)}
                    onToggleSelect={toggleSelect}
                    onDeleteVideo={handleDeleteVideo}
                  />
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4 p-4">
            {currentVideos.map((video) => (
              <VideoCard
                key={video.id}
                video={video}
                isChecked={selected.includes(video.id)}
                onToggleSelect={toggleSelect}
                onDeleteVideo={handleDeleteVideo}
              />
            ))}
          </div>
        )}
        
        <VideoPagination
          currentPage={currentPage}
          totalItems={localVideos.length}
          itemsPerPage={itemsPerPage}
          onPageChange={setCurrentPage}
        />
      </div>
    </div>
  );
}
